import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowUpRight, CheckCircle2, Dumbbell, Film, GraduationCap, Play } from "lucide-react";
import { Marquee, MaskedLines, Reveal, SectionHeading, StatCircle } from "@/components/motion";
import { ApplyForm } from "@/components/forms";

const IMG = {
  hero: "https://images.unsplash.com/photo-1763494392794-a07d77898569?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODB8MHwxfHNlYXJjaHwzfHxhbWVyaWNhbiUyMGZvb3RiYWxsJTIwcGxheWVyJTIwYWN0aW9uJTIwaGlnaCUyMHNjaG9vbCUyMGF0aGxldGV8ZW58MHx8fHwxNzkxMzM3OTc2fDA&ixlib=rb-4.1.0&q=85",
  football: "https://images.unsplash.com/photo-1681407979620-193f8181f5fa?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODB8MHwxfHNlYXJjaHw0fHxhbWVyaWNhbiUyMGZvb3RiYWxsJTIwcGxheWVyJTIwYWN0aW9uJTIwaGlnaCUyMHNjaG9vbCUyMGF0aGxldGV8ZW58MHx8fHwxNzkxMzM3OTc2fDA&ixlib=rb-4.1.0&q=85",
  mens: "https://images.pexels.com/photos/30053813/pexels-photo-30053813.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  womens: "https://images.unsplash.com/photo-1610561870937-c51dcf9dd314?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzZ8MHwxfHNlYXJjaHwyfHx3b21lbnMlMjBiYXNrZXRiYWxsJTIwcGxheWVyJTIwYWN0aW9uJTIwY291cnR8ZW58MHx8fHwxNzkxMzM3OTgyfDA&ixlib=rb-4.1.0&q=85",
};

const PILLARS = [
  {
    icon: Dumbbell,
    title: "Development",
    text: "Daily strength, speed and position work — your body and your game mature in ways one high school offseason simply can't deliver.",
  },
  {
    icon: Film,
    title: "Exposure",
    text: "A full new season of game film, played in front of college coaches who never got the chance to see you the first time around.",
  },
  {
    icon: GraduationCap,
    title: "Academics",
    text: "Eligibility repair, test preparation and real study habits — so you're admissible to college and stay eligible once you arrive.",
  },
];

const SPORTS = [
  {
    testid: "sport-card-football",
    tag: "Fall · Post-Grad Season",
    title: "Football",
    img: IMG.football,
    points: [
      "Full-contact post-grad season on our outdoor field",
      "Position-specific development, every single day",
      "Every snap filmed for college evaluation",
    ],
  },
  {
    testid: "sport-card-mens-bb",
    tag: "Winter · Post-Grad Season",
    title: "Men's Basketball",
    img: IMG.mens,
    points: [
      "Indoor court — training and games in any weather",
      "High-exposure schedule against prep & post-grad teams",
      "College coaches in the gym all season",
    ],
  },
  {
    testid: "sport-card-womens-bb",
    tag: "Winter · Post-Grad Season",
    title: "Women's Basketball",
    img: IMG.womens,
    points: [
      "A dedicated women's program — not an afterthought",
      "Skill development, strength work and film study",
      "A complete recruiting profile built for every player",
    ],
  },
];

export default function HomePage() {
  const { scrollY } = useScroll();
  const yImg = useTransform(scrollY, [0, 700], [0, 70]);

  return (
    <>
      {/* ---------------- HERO ---------------- */}
      <section data-testid="home-hero" className="bg-grid relative flex min-h-screen items-center overflow-hidden">
        <div className="absolute -top-40 right-[-140px] h-[560px] w-[560px] rounded-full bg-ice/10 blur-[140px]" />
        <div className="absolute bottom-[-160px] left-[-120px] h-[460px] w-[460px] rounded-full bg-crimson/10 blur-[140px]" />

        <div className="relative z-10 mx-auto grid w-full max-w-7xl items-center gap-14 px-5 pb-24 pt-32 sm:px-8 lg:grid-cols-[1.12fr_0.88fr] lg:gap-10">
          <div>
            <motion.p
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="eyebrow mb-6 flex items-center gap-3"
            >
              <span className="h-2 w-2 animate-pulse-dot rounded-full bg-crimson" />
              Tuskegee, Alabama · Post-Grad Academy
            </motion.p>

            <h1 className="font-display text-6xl font-black uppercase leading-[0.9] text-white sm:text-7xl lg:text-8xl">
              <MaskedLines
                delay={0.25}
                lines={[<>Bridge the gap.</>, <span className="text-ice">Earn the offer.</span>]}
              />
            </h1>

            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.7 }}
              className="mt-7 max-w-xl text-base leading-relaxed text-slate-300 sm:text-lg"
            >
              <span className="font-semibold text-white">Alabama Bulldogs Sports Academy</span> is a post-graduate
              sports academy in Tuskegee, Alabama — one extra year of development, exposure and academics that turns
              unsigned high school athletes into college recruits.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.85 }}
              className="mt-9 flex flex-wrap items-center gap-4"
            >
              <Link to="/#apply" data-testid="hero-apply-btn" className="btn-primary">
                Apply Now <ArrowRight className="h-4 w-4" />
              </Link>
              <a href="#postgrad" data-testid="hero-learn-btn" className="btn-ghost">
                What is Post-Grad?
              </a>
            </motion.div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8, delay: 1.05 }}
              className="mt-12 grid max-w-lg grid-cols-3 gap-4 border-t border-white/10 pt-6"
            >
              {[
                ["3", "Programs — football & both basketballs"],
                ["1", "Mission: your college placement"],
                ["0", "Athletes left to figure it out alone"],
              ].map(([n, t]) => (
                <div key={t}>
                  <p className="font-display text-3xl font-black text-ice">{n}</p>
                  <p className="mt-1 text-xs leading-snug text-slate-400">{t}</p>
                </div>
              ))}
            </motion.div>
          </div>

          <motion.div style={{ y: yImg }} className="relative hidden lg:block">
            <Reveal delay={0.3} y={40}>
              <div className="clip-notch relative border border-white/10">
                <img
                  src={IMG.hero}
                  alt="Post-graduate football athlete in action"
                  className="h-[540px] w-full object-cover contrast-110 saturate-[.8]"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-navy-900 via-navy-900/10 to-navy-900/30" />
                <div className="absolute bottom-5 left-5 flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-ice">
                  <Play className="h-3.5 w-3.5" /> Post-grad film · every game
                </div>
              </div>
              <div className="glass absolute -left-6 top-10 flex items-center gap-3 rounded-xl px-4 py-3 animate-float">
                <img src="/logo.png" alt="ASA" className="h-10 w-10 object-contain" />
                <div>
                  <p className="font-display text-sm font-extrabold uppercase text-white">The Bulldogs</p>
                  <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-400">ASA · Tuskegee, AL</p>
                </div>
              </div>
              <div className="glass absolute -bottom-6 right-8 rounded-xl px-5 py-3 animate-float-slow">
                <p className="font-display text-2xl font-black text-ice">100%</p>
                <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-slate-400">
                  Film + exposure, every athlete
                </p>
              </div>
            </Reveal>
          </motion.div>
        </div>
      </section>

      <Marquee
        items={[
          "Football",
          "Men's Basketball",
          "Women's Basketball",
          "College Placement",
          "NIL Guidance",
          "Tuskegee, Alabama",
          "Post-Grad · Gap Year",
          "Class of 2027",
        ]}
      />

      {/* ---------------- WHAT IS POST-GRAD ---------------- */}
      <section id="postgrad" className="relative border-t border-white/5 py-24 sm:py-28">
        <div className="mx-auto grid max-w-7xl gap-14 px-5 sm:px-8 lg:grid-cols-[1fr_1.05fr]">
          <div>
            <SectionHeading
              eyebrow="New to post-grad sports?"
              title={
                <>
                  One extra year. <span className="text-ice">A whole new story.</span>
                </>
              }
            />
            <Reveal delay={0.15}>
              <p className="mt-7 text-base leading-relaxed text-slate-300 sm:text-lg">
                Post-graduate sports — some people call it a <span className="text-white">gap year</span> — is one
                additional year of athletic and academic preparation between high school and college.
              </p>
              <p className="mt-4 text-base leading-relaxed text-slate-300 sm:text-lg">
                Instead of jumping into a college program you aren't physically, academically or mentally ready for
                yet, you spend a year at the academy: you train, you compete, you take classes, and you build a brand
                new season of college film. When the year ends, you enter recruiting with a better body, a better
                transcript and better footage than you had as a senior.
              </p>
              <Link
                to="/about"
                data-testid="postgrad-read-more-link"
                className="link-underline mt-7 inline-flex items-center gap-2 font-display text-base font-bold uppercase tracking-wider text-ice"
              >
                Read the full breakdown <ArrowUpRight className="h-4 w-4" />
              </Link>
            </Reveal>
          </div>
          <div className="grid content-start gap-5">
            {PILLARS.map((p, i) => (
              <Reveal key={p.title} delay={0.1 + i * 0.1} className="h-full">
                <div
                  data-testid={`pillar-card-${p.title.toLowerCase()}`}
                  className="group flex gap-5 rounded-2xl border border-white/10 bg-navy-800 p-6 transition-colors hover:border-ice/50"
                >
                  <div className="grid h-12 w-12 shrink-0 place-items-center rounded-xl border border-ice/30 bg-ice/10 text-ice transition-colors group-hover:bg-ice group-hover:text-navy-900">
                    <p.icon className="h-6 w-6" />
                  </div>
                  <div>
                    <h3 className="font-display text-xl font-extrabold uppercase tracking-wide text-white">
                      {p.title}
                    </h3>
                    <p className="mt-2 text-sm leading-relaxed text-slate-300">{p.text}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ---------------- THE ODDS ---------------- */}
      <section data-testid="odds-section" className="relative border-t border-white/5 bg-navy-850 py-24 sm:py-28">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <SectionHeading
            align="center"
            eyebrow="The odds — honestly"
            title={
              <>
                Most athletes never get <span className="text-crimson-bright">told the truth.</span>
              </>
            }
            description="Here's the straight math on the path from high school to college sports — and exactly where a post-graduate year changes it."
          />
          <div className="mt-16 grid gap-14 sm:grid-cols-3 sm:gap-6 lg:gap-10">
            <StatCircle
              testid="probability-circle-7"
              value={7}
              suffix="%"
              tone="#94A3B8"
              label="of high school athletes move on to compete in college straight out of high school"
              sub="NCAA participation research"
            />
            <StatCircle
              testid="probability-circle-100"
              value={100}
              suffix="%"
              tone="#38BDF8"
              label="of ASA post-grads finish the year with new film, college exposure and academic preparation"
              sub="Our commitment to every athlete"
              delay={0.15}
            />
            <StatCircle
              testid="probability-circle-1"
              value={1}
              suffix=""
              tone="#E11D48"
              label="extra year to change your body, your transcript and your recruiting board"
              sub="The post-graduate gap year"
              delay={0.3}
            />
          </div>
        </div>
      </section>

      {/* ---------------- SPORTS ---------------- */}
      <section id="sports" className="border-t border-white/5 py-24 sm:py-28">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <SectionHeading
            eyebrow="The programs"
            title={
              <>
                Three sports. <span className="text-ice">One standard.</span>
              </>
            }
            description="We offer post-graduate football, men's basketball and women's basketball — full seasons of real competition, filmed and built for college recruiting."
          />
          <div className="mt-14 grid gap-6 md:grid-cols-3">
            {SPORTS.map((s, i) => (
              <Reveal key={s.testid} delay={i * 0.1} className="h-full">
                <div
                  data-testid={s.testid}
                  className="group flex h-full flex-col overflow-hidden rounded-2xl border border-white/10 bg-navy-800 transition-colors hover:border-ice/50"
                >
                  <div className="relative h-52 shrink-0 overflow-hidden">
                    <img
                      src={s.img}
                      alt={s.title}
                      loading="lazy"
                      className="h-full w-full object-cover contrast-110 saturate-[.8] transition-transform duration-700 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-navy-800 via-navy-800/10 to-transparent" />
                    <span className="glass absolute left-4 top-4 rounded-full px-3 py-1 font-mono text-[10px] uppercase tracking-[0.2em] text-ice">
                      {s.tag}
                    </span>
                  </div>
                  <div className="flex flex-1 flex-col p-6">
                    <h3 className="font-display text-2xl font-extrabold uppercase text-white">{s.title}</h3>
                    <ul className="mt-4 flex-1 space-y-2.5">
                      {s.points.map((p) => (
                        <li key={p} className="flex gap-2.5 text-sm leading-relaxed text-slate-300">
                          <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-ice" />
                          {p}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ---------------- COLLEGE PLACEMENT / FILM ---------------- */}
      <section
        id="placement"
        data-testid="film-breakdown-section"
        className="border-t border-white/5 bg-navy-850 py-24 sm:py-28"
      >
        <div className="mx-auto grid max-w-7xl items-center gap-14 px-5 sm:px-8 lg:grid-cols-2">
          <div>
            <SectionHeading
              eyebrow="College placement"
              title={
                <>
                  Two seasons of film. <span className="text-ice">One complete prospect.</span>
                </>
              }
            />
            <Reveal delay={0.15}>
              <p className="mt-7 text-base leading-relaxed text-slate-300 sm:text-lg">
                College coaches recruit off film. Our staff takes every athlete's{" "}
                <span className="text-white">high school film</span> and combines it with their{" "}
                <span className="text-white">post-graduate film</span> into one complete recruiting profile — cleaned
                up, cut into highlights, and sent directly to college coaches who need your position.
              </p>
              <p className="mt-4 text-base leading-relaxed text-slate-300 sm:text-lg">
                One year in Tuskegee gives coaches two full seasons of proof instead of one — and gives your family a
                staff that makes the calls, sends the tapes and follows up until the answer comes back.
              </p>
            </Reveal>
          </div>

          <div className="relative mx-auto w-full max-w-md">
            <motion.div
              initial={{ opacity: 0, x: -40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
              className="clip-notch border border-white/10 bg-navy-700 p-5"
            >
              <div className="flex items-center justify-between">
                <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-silver-dim">High School Film</p>
                <p className="font-mono text-[11px] text-ice">2026 Season</p>
              </div>
              <div className="mt-4 space-y-2">
                {[80, 55, 70, 40].map((w, i) => (
                  <div key={i} className="h-1.5 rounded-full bg-slate-600/40" style={{ width: `${w}%` }} />
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.7, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
              className="clip-notch mt-4 border border-ice/40 bg-navy-600 p-5"
            >
              <div className="flex items-center justify-between">
                <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-ice">Post-Grad Film</p>
                <p className="font-mono text-[11px] text-ice">2027 Season</p>
              </div>
              <div className="mt-4 space-y-2">
                {[95, 70, 85, 60, 75].map((w, i) => (
                  <div key={i} className="h-1.5 rounded-full bg-ice/50" style={{ width: `${w}%` }} />
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.7, delay: 0.3, ease: [0.16, 1, 0.3, 1] }}
              className="mt-6 rounded-xl border border-crimson/40 bg-crimson/10 p-5 text-center"
            >
              <p className="font-display text-xl font-extrabold uppercase tracking-wide text-white">
                Complete prospect profile
              </p>
              <p className="mt-1 font-mono text-[11px] uppercase tracking-[0.2em] text-crimson-bright">
                Cut · Sent · Pitched to college coaches
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ---------------- MISSION ---------------- */}
      <section className="relative overflow-hidden border-t border-white/5 py-24 sm:py-32">
        <img
          src="/logo.png"
          alt=""
          aria-hidden="true"
          className="pointer-events-none absolute -right-24 top-1/2 w-[420px] max-w-none -translate-y-1/2 opacity-[0.05]"
        />
        <div className="mx-auto max-w-5xl px-5 text-center sm:px-8">
          <Reveal>
            <p className="eyebrow mb-8">Our mission</p>
            <p
              data-testid="mission-statement-card"
              className="font-display text-2xl font-bold uppercase leading-snug text-silver sm:text-3xl lg:text-4xl"
            >
              "Our mission is to bridge the gap between potential and opportunity by providing post-graduate
              student-athletes with the <span className="text-white">development, discipline, exposure, and academic
              preparation</span> necessary to earn opportunities at the collegiate level. We are committed to developing
              the complete student-athlete — building stronger competitors, better students, and individuals of{" "}
              <span className="text-ice">character</span> prepared to succeed in college and beyond."
            </p>
            <p className="mt-8 font-mono text-xs uppercase tracking-[0.24em] text-slate-500">
              — Alabama Bulldogs Sports Academy
            </p>
          </Reveal>
        </div>
      </section>

      {/* ---------------- APPLY NOW ---------------- */}
      <section id="apply" data-testid="apply-now-section" className="border-t border-white/5 bg-navy-850 py-24 sm:py-28">
        <div className="mx-auto grid max-w-7xl gap-14 px-5 sm:px-8 lg:grid-cols-[0.85fr_1.15fr]">
          <div>
            <SectionHeading
              eyebrow="Apply now"
              title={
                <>
                  The roster starts <span className="text-ice">with you.</span>
                </>
              }
              description="Fill out the application and our staff reaches out to you and your parents directly. Tell us who you are — the film conversation comes next."
            />
            <div className="mt-10 space-y-6">
              {[
                ["01", "We review your application", "Every submission is read by our staff — within days, not months."],
                ["02", "We call you and your family", "Athletes and parents hear the same plan at the same time."],
                ["03", "You visit the academy", "Walk the field, the court and the kitchen here in Tuskegee."],
              ].map(([n, t, d], i) => (
                <Reveal key={n} delay={0.1 + i * 0.1}>
                  <div className="flex gap-5">
                    <span className="font-mono text-sm font-semibold text-crimson-bright">{n}</span>
                    <div>
                      <h3 className="font-display text-lg font-extrabold uppercase tracking-wide text-white">{t}</h3>
                      <p className="mt-1 text-sm leading-relaxed text-slate-400">{d}</p>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
          <Reveal delay={0.2}>
            <div className="glass rounded-2xl p-6 sm:p-8">
              <ApplyForm />
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
